/**
 * UserContext -- single source of truth for the current user's identity,
 * employee record, and active unit/incident assignment.
 *
 * Reads identity from the offline cache only -- no direct Supabase queries.
 * SyncManager is responsible for populating the cache. UserContext holds
 * loading: true and waits for a sync-complete event when the cache is empty.
 *
 * Provider placement: inside RouteEnforcer -- wraps all routes.
 */

import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createClient } from '@/lib/supabase/client'
import type { Employee } from '@/lib/services/employees'
import { smartSync } from '@/lib/syncManager'
import { AppError } from '@/lib/appError'
import type { Tables } from '#modules/database'

// -- Sign-out -----------------------------------------------------------------

// Set while a sign-out this app initiated is in flight.
let expectingSignOut = false

// The app's deliberate sign-out: arm the flag, then end the session.
export async function signOut(): Promise<void> {
  expectingSignOut = true
  try {
    await createClient().auth.signOut()
  } catch {
    // No event is coming to consume the flag; clear it so a later unrequested
    // SIGNED_OUT is not misread as expected.
    expectingSignOut = false
  }
}

// Reads and clears the flag; true only for a sign-out this app initiated.
export function consumeExpectedSignOut(): boolean {
  const was = expectingSignOut
  expectingSignOut = false
  return was
}

// True only when a no-user event is a mid-session loss to surface as a crash:
// a SIGNED_OUT (not INITIAL_SESSION), with a user already established, that the
// app did not initiate.
export function isUnexpectedSignOut(
  event: string,
  hadUser: boolean,
  wasExpected: boolean,
): boolean {
  return event === 'SIGNED_OUT' && hadUser && !wasExpected
}

// -- Types --------------------------------------------------------------------

export type UserIncidentUnit = Pick<Tables<'incident_units'>,
  'id' |
  'unit_id' |
  'incident_id'
>

export type UserUnit = Pick<Tables<'units'>,
  'id' |
  'name'
>

export type UserIncident = Pick<Tables<'incidents'>,
  'id' |
  'name'
>

export type UserState = {
  user: { id: string; email?: string } | null
  employee: Employee | null
  incidentUnit: UserIncidentUnit | null
  unit: UserUnit | null
  incident: UserIncident | null
  /** true while the initial cache read or sync is in flight */
  loading: boolean
  mustChangePassword: boolean
}

// -- Context ------------------------------------------------------------------

const UserContext = createContext<UserState | null>(null)

// -- Cache read ---------------------------------------------------------------

type CacheResult = {
  emp: Employee | null
  incidentUnit: UserIncidentUnit | null
  unit: UserUnit | null
  incident: UserIncident | null
}

async function readFromCache(): Promise<CacheResult> {
  const { getCachedCurrentEmployee } = await import('@/lib/offlineStore')
  const cached = await getCachedCurrentEmployee().catch(() => null)
  return {
    emp: cached?.employee ?? null,
    unit: cached?.unit ?? null,
    incidentUnit: cached?.incidentUnit ?? null,
    incident: cached?.incident ?? null,
  }
}

// -- State helpers -----------------------------------------------------------

const SIGNED_OUT_STATE: UserState = {
  user: null, employee: null, incidentUnit: null,
  unit: null, incident: null, loading: false, mustChangePassword: false,
}

function buildSignedInState(user: any, result: CacheResult, mustChangePassword: boolean): UserState {
  return {
    user: { id: user.id, email: user.email },
    employee: result.emp,
    incidentUnit: result.incidentUnit,
    unit: result.unit,
    incident: result.incident,
    loading: false,
    mustChangePassword,
  }
}

// -- Provider -----------------------------------------------------------------

// Thrown on an unexpected sign-out; offers return-to-login plus a cache clear.
const UNEXPECTED_SIGN_OUT_ERROR = new AppError(
  'You were signed out unexpectedly. This can happen after a connection drop during sign-in. Please sign in again; if it keeps happening, clear the cache.',
  ['signout', 'clearcache'],
)

export function UserProvider({ children }: { children: ReactNode }) {
  const currentUserIdRef = useRef<string | null>(null)
  // When set, rethrown during render so the nearest error boundary handles it.
  const [unexpectedSignOut, setUnexpectedSignOut] = useState<AppError | null>(null)
  if (unexpectedSignOut) throw unexpectedSignOut

  // Initial state loading to wait for first auth state change
  const [state, setState] = useState<UserState>({
    user: null,
    employee: null,
    incidentUnit: null,
    unit: null,
    incident: null,
    loading: true,
    mustChangePassword: false,
  })

  useEffect(() => {
    const supabase = createClient()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: string, session: any) => {
        const user = session?.user ?? null
        const mustChangePassword = !!user?.user_metadata?.must_change_password

        console.log(`[Auth] State change - ${event} - ${user ? 'with user' : 'no user'}`)

        if (!user) {
          const hadUser = currentUserIdRef.current !== null
          currentUserIdRef.current = null
          setState(SIGNED_OUT_STATE)
          // Consume the latch only for SIGNED_OUT, so another event can't drain
          // a flag armed for a later one.
          const wasExpected = event === 'SIGNED_OUT' && consumeExpectedSignOut()
          if (isUnexpectedSignOut(event, hadUser, wasExpected)) {
            setUnexpectedSignOut(UNEXPECTED_SIGN_OUT_ERROR)
          }
          return
        }

        if (user.id === currentUserIdRef.current) {
          // Same user (token refresh, etc.) -- update mustChangePassword, quietly refresh operational data
          setState(s => s.mustChangePassword !== mustChangePassword ? { ...s, mustChangePassword } : s)
          setTimeout(() => { smartSync({ full: false }).catch(() => {}) }, 0)
          return
        }

        // New user (first load, login, user switch)
        currentUserIdRef.current = user.id
        setState(s => ({ ...s, loading: true }))
        // Deferred: Supabase v2 holds the auth lock while awaiting this callback.
        setTimeout(async () => {
          await smartSync()
          const cacheResult = await readFromCache()
          if (currentUserIdRef.current !== user.id) return
          setState(buildSignedInState(user, cacheResult, mustChangePassword))
        }, 0)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  return (
    <UserContext.Provider value={{ ...state }}>
      {children}
    </UserContext.Provider>
  )
}

// -- Hook ---------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components
export function useUser(): UserState {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error('useUser() must be used inside <UserProvider>')
  return ctx
}
